// Slides the inventory status from oldHtml (default: what is shown now) to newHtml.
// Only one animation runs per element: starting a new one first settles the previous one
// (clearing its timer/listener) so a stale callback can never overwrite the new state,
// and identical text is swapped silently instead of sliding the same words out and in.
(() => {
  const DURATION_MS = 280; // keep in sync with --pi-inventory-spin-duration in section-main-product.css

  window.__piAnimateInventory = (destination, newHtml, oldHtml) => {
    destination._piInventoryCancel?.();

    const from = (oldHtml ?? destination.innerHTML).trim();
    const to = newHtml.trim();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (from === to || !from || reduceMotion) {
      if (destination.innerHTML.trim() !== to) destination.innerHTML = to;
      return;
    }

    const lockedHeight = destination.getBoundingClientRect().height;
    if (lockedHeight) {
      destination.style.height = `${lockedHeight}px`;
      destination.style.minHeight = `${lockedHeight}px`;
    }

    const oldContent = document.createElement('span');
    oldContent.innerHTML = from;
    oldContent.classList.add('pi-inventory-spin-out');

    const newContent = document.createElement('span');
    newContent.innerHTML = to;
    newContent.classList.add('pi-inventory-spin-in');

    destination.classList.add('is-animating');
    destination.innerHTML = '';
    destination.appendChild(oldContent);
    destination.appendChild(newContent);

    let timer;
    const onEnd = (event) => {
      if (event.target === newContent) finish();
    };
    const finish = () => {
      window.clearTimeout(timer);
      newContent.removeEventListener('animationend', onEnd);
      destination._piInventoryCancel = null;
      // Same task: the class removal and final markup land in one paint
      destination.classList.remove('is-animating');
      destination.innerHTML = to;
      destination.style.removeProperty('height');
      destination.style.removeProperty('min-height');
    };

    destination._piInventoryCancel = finish;
    newContent.addEventListener('animationend', onEnd);
    timer = window.setTimeout(finish, DURATION_MS + 150);
  };
})();

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      _lastOptionSelectionKey = null;
      _lastOptionSelectionAt = 0;
      _inFlightOptionSelectionKey = null;
      _inFlightOptionSelectionTimer = null;
      _optionSelectionLoopWindowStart = 0;
      _optionSelectionLoopCount = 0;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.syncMediaAspectRatioFromFirstImage();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          try {
            window?.Shopify?.PaymentButton?.init();
          } catch (error) {
            console.error(error);
          }
          try {
            window?.ProductModel?.loadShopifyXR();
          } catch (error) {
            console.error(error);
          }
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const now = Date.now();
        const optionKey = Array.isArray(selectedOptionValues) ? selectedOptionValues.join(',') : '';
        const productUrlFromTarget = target?.dataset?.productUrl || '';
        const selectionKey = `${this.dataset.section || ''}|${productUrlFromTarget}|${optionKey}`;

        if (this._lastOptionSelectionKey === selectionKey && now - this._lastOptionSelectionAt < 250) return;
        this._lastOptionSelectionKey = selectionKey;
        this._lastOptionSelectionAt = now;

        if (!this._optionSelectionLoopWindowStart || now - this._optionSelectionLoopWindowStart > 2000) {
          this._optionSelectionLoopWindowStart = now;
          this._optionSelectionLoopCount = 0;
        }
        this._optionSelectionLoopCount += 1;
        if (this._optionSelectionLoopCount > 25) {
          this.abortController?.abort();
          this.pendingRequestUrl = null;
          return;
        }

        if (this._inFlightOptionSelectionKey === selectionKey) return;
        this._inFlightOptionSelectionKey = selectionKey;
        window.clearTimeout(this._inFlightOptionSelectionTimer);
        this._inFlightOptionSelectionTimer = window.setTimeout(() => {
          if (this._inFlightOptionSelectionKey === selectionKey) this._inFlightOptionSelectionKey = null;
        }, 3000);

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const baseCallback = shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl);
        const callback = (html) => {
          try {
            baseCallback(html);
          } finally {
            if (this._inFlightOptionSelectionKey === selectionKey) this._inFlightOptionSelectionKey = null;
          }
        };

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback,
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm?.submitButton && !productForm.submitButton.classList.contains('loading')) {
          productForm.submitButton.setAttribute('aria-disabled', 'true');
        }
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            } else {
              console.error(error);
            }
            const productForm = this.productForm;
            if (productForm?.submitButton && !productForm.submitButton.classList.contains('loading')) {
              productForm.submitButton.removeAttribute('aria-disabled');
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          const oldSelects = this.variantSelectors;
          const pillSelector = '.product-form__input--pill input[type="radio"] + label';

          // The swap replaces the pills mid-transition. Remember their current (animated) colours so the
          // new pills can continue from there instead of jumping straight to the end state.
          const oldLabels = Array.from(oldSelects.querySelectorAll(pillSelector));
          const oldColors = oldLabels.map((label) => {
            const style = getComputedStyle(label);
            return {
              backgroundColor: style.backgroundColor,
              color: style.color,
              borderColor: style.borderColor,
            };
          });

          const continuePillTransitions = (newNode) => {
            const newLabels = Array.from(newNode.querySelectorAll(pillSelector));
            if (newLabels.length !== oldColors.length) return;

            newLabels.forEach((label, i) => {
              Object.assign(label.style, oldColors[i], { transition: 'none' });
            });
            newNode.getBoundingClientRect(); // commit the starting colours
            newLabels.forEach((label) => {
              label.style.removeProperty('transition');
              label.style.removeProperty('background-color');
              label.style.removeProperty('color');
              label.style.removeProperty('border-color');
              if (!label.getAttribute('style')) label.removeAttribute('style');
            });
          };

          HTMLUpdateUtility.viewTransition(
            oldSelects,
            variantSelects,
            this.preProcessHtmlCallbacks,
            [continuePillTransitions]
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.syncMediaAspectRatioFromFirstImage();

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              if (id === 'Inventory') {
                // Fill the ship date on the detached markup so it's complete when the animation starts
                window.__piUpdateReadyToShipMessages?.(source);
                window.__piSaveInventoryStatus?.(source);
                // Clear any ongoing animation state immediately
                destination.classList.remove('is-animating');

                if (!destination.classList.contains('hidden') && source.innerText.trim() !== '') {
                  const lockedHeight = destination.getBoundingClientRect().height;
                  if (lockedHeight) {
                    destination.style.height = `${lockedHeight}px`;
                    destination.style.minHeight = `${lockedHeight}px`;
                  }

                  const oldContent = document.createElement('span');
                  oldContent.innerHTML = destination.innerHTML.trim();
                  oldContent.classList.add('pi-inventory-spin-out');

                  const newContent = document.createElement('span');
                  newContent.innerHTML = source.innerHTML.trim();
                  newContent.classList.add('pi-inventory-spin-in');

                  destination.classList.add('is-animating');
                  destination.innerHTML = '';
                  destination.appendChild(oldContent);
                  destination.appendChild(newContent);

                  const finishAnimation = () => {
                    if (destination.classList.contains('is-animating')) {
                      destination.classList.remove('is-animating');
                      destination.innerHTML = source.innerHTML;
                    }
                    destination.style.removeProperty('height');
                    destination.style.removeProperty('min-height');
                    newContent.removeEventListener('animationend', finishAnimation);
                  };

                  newContent.addEventListener('animationend', finishAnimation);
                  // Safety timeout
                  setTimeout(finishAnimation, 500);
                } else {
                  destination.innerHTML = source.innerHTML;
                  destination.classList.toggle('hidden', shouldHide(source));
                }
              } else {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const shouldDisableSubmit =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
          this.productForm?.toggleSubmitButton(shouldDisableSubmit, window.variantStrings.soldOut);

          const liveForm = this.productForm?.form;
          if (liveForm) {
            const livePreorderInput = liveForm.querySelector('.product-preorder-property');
            const newPreorderInput = html.querySelector('.product-preorder-property');
            if (newPreorderInput && !livePreorderInput) {
              liveForm.querySelector('.product-variant-id')?.after(newPreorderInput.cloneNode(true));
            } else if (!newPreorderInput && livePreorderInput) {
              livePreorderInput.remove();
            }
          }

          this.querySelector('.product-form__buttons')?.classList.toggle('pi-atc-disabled', shouldDisableSubmit);
          if (this.productForm?.submitButton && !this.productForm.submitButton.classList.contains('loading')) {
            this.productForm.submitButton.removeAttribute('aria-disabled');
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      syncMediaAspectRatioFromFirstImage() {
        if (!this.querySelector('.product-media-container.media-fit-cover')) return;

        const containers = Array.from(
          this.querySelectorAll('.product__media-list .product-media-container.media-type-image')
        );
        if (!containers.length) return;

        const first = containers[0];
        const firstPreviewRatio = (first.style.getPropertyValue('--preview-ratio') || '').trim();
        const firstRatio = (first.style.getPropertyValue('--ratio') || '').trim();
        if (!firstPreviewRatio && !firstRatio) return;

        containers.forEach((el) => {
          if (firstPreviewRatio) el.style.setProperty('--preview-ratio', firstPreviewRatio);
          if (firstRatio) el.style.setProperty('--ratio', firstRatio);
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.querySelector('.product-form__buttons')?.classList.add('pi-atc-disabled');

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// On page load, show the inventory status remembered from the previous product page
// (sessionStorage), then spin to the current one (reuses the variant-change animation)
(() => {
  const STORAGE_KEY = 'pi-last-inventory-status';
  const readStored = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };
  const save = (html) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, html);
    } catch (e) {}
  };
  // Lets the variant-change code keep the remembered status up to date
  window.__piSaveInventoryStatus = (el) => {
    if (el && el.innerText.trim() !== '') save(el.innerHTML.trim());
  };

  const run = () => {
    document.querySelectorAll('.product__inventory').forEach((destination) => {
      // If the early inline script already swapped in the previous status, the current one is in data-pi-current
      const stashedCurrent = destination.dataset.piCurrent;
      const previousHtml = stashedCurrent ? destination.innerHTML.trim() : readStored();
      if (!stashedCurrent) window.__piUpdateReadyToShipMessages?.(destination);
      const html = stashedCurrent || destination.innerHTML.trim();
      delete destination.dataset.piCurrent;
      if (destination.classList.contains('hidden') || destination.innerText.trim() === '') return;

      save(html);

      // Nothing remembered (first product viewed this session): show it as-is, no animation
      if (!previousHtml) return;

      const lockedHeight = destination.getBoundingClientRect().height;
      if (lockedHeight) {
        destination.style.height = `${lockedHeight}px`;
        destination.style.minHeight = `${lockedHeight}px`;
      }

      const oldContent = document.createElement('span');
      oldContent.innerHTML = previousHtml;
      oldContent.classList.add('pi-inventory-spin-out');

      const newContent = document.createElement('span');
      newContent.innerHTML = html;
      newContent.classList.add('pi-inventory-spin-in');

      destination.classList.add('is-animating');
      destination.innerHTML = '';
      destination.appendChild(oldContent);
      destination.appendChild(newContent);

      const finishAnimation = () => {
        if (destination.classList.contains('is-animating')) {
          destination.classList.remove('is-animating');
          destination.innerHTML = html;
        }
        destination.style.removeProperty('height');
        destination.style.removeProperty('min-height');
        newContent.removeEventListener('animationend', finishAnimation);
      };

      newContent.addEventListener('animationend', finishAnimation);
      setTimeout(finishAnimation, 500);
    });
  };
  const start = () => run();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();